import { datadogRum } from '@datadog/browser-rum'
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'

import { useBabylonUser } from '@babylon/babylon-user'

import { useSendbirdToken } from '~/core/auth/Authentication/Sendbird/useSendbirdToken'

import { fetchTotalUnreadMessageCount } from './sendbirdRestClient'
import { usePollingUnreadCount } from './usePollingUnreadCount'

interface UnreadCountContextType {
  unreadCount: number
  /** Directly set the count (e.g. when a real-time event updates it). */
  setUnreadCount: (count: number) => void
  /** Fetch the latest unread count from Sendbird. Safe to call frequently
   *  — concurrent calls are deduplicated via inFlightRef. */
  refreshUnreadCount: () => Promise<void>
  /** Whether the Sendbird UIKit websocket is currently connected. */
  isWebsocketActive: boolean
  /** Signal that the websocket connection is active/inactive. */
  setWebsocketActive: (active: boolean) => void
  /** Restart the 60-second polling timer from zero. Call this after
   *  any event that already supplies a fresh count (navigation,
   *  websocket push) so the next poll is a full interval away. */
  resetPollingTimer: () => void
}

const UnreadCountContext = createContext<UnreadCountContextType | null>(null)

/**
 * Provides a shared unread-message count and a way to refresh it.
 * Uses sendbirdRestClient (a standalone SDK singleton) rather than UIKit,
 * so it can work outside of the chat UI (e.g. nav badges).
 *
 * Also manages a 60-second polling interval that automatically pauses
 * while the Sendbird websocket is actively connected (i.e. the chat
 * page is open and receiving real-time events).
 */
export const UnreadCountProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [unreadCount, setUnreadCount] = useState(0)
  const [isWebsocketActive, setWebsocketActive] = useState(false)
  const { retrieveToken } = useSendbirdToken()
  const currentUser = useBabylonUser()

  // Flipping this value restarts the polling interval from zero.
  // Used after navigation refreshes or websocket pushes so the next
  // poll is a full 60 seconds away rather than firing immediately.
  // The value doesn't matter, just that it changes whenever we want to reset the timer.
  const [pollingResetSignal, setPollingResetSignal] = useState(false)
  const resetPollingTimer = useCallback(() => {
    setPollingResetSignal((prev) => !prev)
  }, [])

  // Tracks the in-flight request so we can deduplicate concurrent calls.
  // Multiple components may call refreshUnreadCount at roughly the same
  // time (e.g. on mount + on channel change); this ensures only one
  // network round-trip happens and all callers await the same promise.
  const inFlightRef = useRef<Promise<void> | null>(null)

  const refreshUnreadCount = useCallback((): Promise<void> => {
    if (!currentUser?.uuid) {
      return Promise.resolve()
    }

    // If a fetch is already in progress, piggyback on it instead of
    // firing a duplicate request.
    if (inFlightRef.current) {
      return inFlightRef.current
    }

    const request = (async () => {
      try {
        // Always retrieve a fresh token rather than reusing one that may
        // already be bound to another Sendbird SDK instance (e.g. the
        // SendBirdProvider used by the chat UI). Using the same token
        // across multiple concurrent connections can cause session conflicts.
        const freshToken = await retrieveToken()
        if (!freshToken) return

        // fetch unread count via the REST API
        const count = await fetchTotalUnreadMessageCount(
          currentUser.uuid,
          freshToken,
          retrieveToken
        )
        setUnreadCount(count)
      } catch (err) {
        datadogRum.addError(err as Error, {
          source: 'unread-count-provider',
          action: 'refresh_unread_count',
        })
      }
    })()

    // Store the promise so concurrent callers can reuse it, and clear
    // it once settled (success or failure) to allow future refreshes.
    // The identity check prevents a future refactor from accidentally
    // clearing a newer in-flight request.
    inFlightRef.current = request
    request.finally(() => {
      if (inFlightRef.current === request) {
        inFlightRef.current = null
      }
    })

    return request
  }, [currentUser, retrieveToken])

  // Poll every 60 s when the websocket is NOT active.
  // The interval resets whenever pollingResetSignal is bumped (e.g.
  // after a navigation refresh or a websocket-pushed count).
  usePollingUnreadCount(
    refreshUnreadCount,
    isWebsocketActive,
    pollingResetSignal
  )

  return (
    <UnreadCountContext.Provider
      value={{
        unreadCount,
        setUnreadCount,
        refreshUnreadCount,
        isWebsocketActive,
        setWebsocketActive,
        resetPollingTimer,
      }}
    >
      {children}
    </UnreadCountContext.Provider>
  )
}

export const useUnreadCount = (): UnreadCountContextType => {
  const ctx = useContext(UnreadCountContext)
  if (!ctx) {
    throw new Error('useUnreadCount must be used within an UnreadCountProvider')
  }
  return ctx
}
